<template>
  <div class="cultures-pie-chart">
    <div class="chart-header">
      <span class="chart-title">Répartition des surfaces par culture</span>
    </div>
    <div v-if="loading" class="chart-loading">
      <i class="el-icon-loading" />
      <p>Chargement des données...</p>
    </div>
    <div v-else class="chart-content">
      <div class="chart-wrapper">
        <Doughnut
          :chart-data="chartData"
          :chart-options="chartOptions"
          :height="null"
        />
      </div>
      <div v-if="chartData.labels && chartData.labels.length > 0" class="custom-legend">
        <div
          v-for="(label, index) in chartData.labels"
          :key="index"
          class="legend-item"
        >
          <span
            class="legend-color"
            :style="{ backgroundColor: getLegendColor(index) }"
          />
          <span class="legend-label">{{ getLegendLabel(index) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { mapGetters } from 'vuex';
import DashboardAPI from '@/api/dashboard';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

export default {
  name: 'CulturesPieChart',
  components: {
    Doughnut,
  },
  data() {
    return {
      loading: false,
      chartData: {
        labels: [],
        datasets: [],
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', ['filters']),
    ...mapGetters('camp_agricole', ['camp_id']),
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
            bottom: 10,
            right: 20,
            left: 20,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          title: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label(context) {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${label}: ${value.toFixed(2)} ha (${percentage}%)`;
              },
            },
          },
        },
      };
    },
  },
  watch: {
    filters: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    camp_id: {
      handler() {
        this.fetchData();
      },
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    buildApiFilters() {
      const activeFilters = this.filters || {};
      const niveau = activeFilters.niveau || {};

      return {
        campagne_id: this.camp_id || null,
        periode: {
          type: activeFilters.periode?.type || 'campagne',
          date_debut: activeFilters.periode?.date_debut || null,
          date_fin: activeFilters.periode?.date_fin || null,
        },
        ci_id: niveau.ci_id !== undefined && niveau.ci_id !== null ? niveau.ci_id : null,
        antenne_id: niveau.antenne_id !== undefined && niveau.antenne_id !== null ? niveau.antenne_id : null,
        groupement_id: niveau.groupement_id !== undefined && niveau.groupement_id !== null ? niveau.groupement_id : null,
        producteur_id: niveau.producteur_id !== undefined && niveau.producteur_id !== null ? niveau.producteur_id : null,
        cultures: (activeFilters.cultures || [])
          .map(id => {
            if (typeof id === 'number') {
              return id;
            }
            if (typeof id === 'object' && id !== null && id.id) {
              return typeof id.id === 'number' ? id.id : parseInt(id.id, 10);
            }
            if (typeof id === 'string') {
              const numId = parseInt(id, 10);
              return isNaN(numId) ? null : numId;
            }
            return id;
          })
          .filter(id => id !== null && id !== undefined && !isNaN(id)),
        certification: activeFilters.certification || 'toutes',
      };
    },
    async fetchData() {
      this.loading = true;
      try {
        const apiFilters = this.buildApiFilters();

        const response = await DashboardAPI.getRepartitionCulturesChart(apiFilters);
        if (response && response.data) {
          this.chartData = {
            labels: response.data.labels || [],
            datasets: response.data.datasets || [],
          };
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données de répartition des cultures:', error);
        this.$message.error('Erreur lors du chargement des données de répartition des cultures');
      } finally {
        this.loading = false;
      }
    },
    getLegendColor(index) {
      if (this.chartData.datasets && this.chartData.datasets[0] && this.chartData.datasets[0].backgroundColor) {
        const colors = this.chartData.datasets[0].backgroundColor;
        if (Array.isArray(colors)) {
          return colors[index % colors.length];
        }
        return colors;
      }
      // Couleurs par défaut
      const defaultColors = [
        '#00ab53', '#00d977', '#3888fa', '#f56c6c', '#e6a23c',
        '#909399', '#67c23a', '#409eff', '#f78989', '#95d475',
        '#00ab53', '#00d977', '#3888fa', '#f56c6c', '#e6a23c',
        '#909399', '#67c23a', '#409eff', '#f78989', '#95d475',
      ];
      return defaultColors[index % defaultColors.length];
    },
    getLegendLabel(index) {
      if (!this.chartData || !this.chartData.labels || !this.chartData.labels.length) {
        return '';
      }
      const label = this.chartData.labels[index] || '';

      if (!this.chartData.datasets || !this.chartData.datasets[0] || !Array.isArray(this.chartData.datasets[0].data)) {
        return label;
      }

      const values = this.chartData.datasets[0].data.map(v => Number(v) || 0);
      const total = values.reduce((sum, v) => sum + v, 0);

      if (!total) {
        return label;
      }

      const value = values[index] || 0;
      const percentage = ((value / total) * 100).toFixed(0);

      return `${label} (${percentage}%)`;
    },
  },
};
</script>

<style lang="scss" scoped>
.cultures-pie-chart {
  width: 100%;
  height: 100%;
  min-height: 500px; // même hauteur minimale que les autres cartes de graphiques
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .chart-header {
    background-color: #00ab53;
    color: #fff;
    padding: 12px 16px;
    border-radius: 4px 4px 0 0;
    flex-shrink: 0;

    .chart-title {
      font-size: 16px;
      font-weight: bold;
      display: block;
    }
  }

  ::v-deep .chartjs-render-monitor {
    max-height: 100%;
    overflow: visible;
  }

  ::v-deep canvas {
    max-width: 100%;
  }

  .chart-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: row;
    gap: 15px;
    padding: 10px;
    overflow: hidden;
  }

  .chart-wrapper {
    flex: 1;
    min-width: 0;
    position: relative;
    overflow: hidden;
    max-width: 60%;

    ::v-deep canvas {
      max-width: 100%;
      max-height: 100%;
    }
  }

  .custom-legend {
    flex: 0 0 260px;
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 5px 10px 5px 5px;
    font-size: 10px;
    line-height: 1.4;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-rows: auto;
    column-gap: 10px;
    row-gap: 6px;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;

      &:hover {
        background: #555;
      }
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;

      .legend-color {
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        border-radius: 2px;
        display: inline-block;
      }

      .legend-label {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #606266;
      }
    }
  }

  .chart-loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #909399;

    i {
      font-size: 32px;
      color: #00ab53;
      animation: rotate 1s linear infinite;
    }

    p {
      margin-top: 16px;
      font-size: 14px;
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

